import * as React from 'react';
import Portal from '@nebula/portal';
import { CSSTransition } from 'react-transition-group';

import cx from 'classnames';
import styles from './styles.scss';

type Props = {
  children: React.ReactNode;
  shouldShow?: boolean; // show / hide popup
  showOverlay?: boolean; // cover the background with shadow or not
  closeOnEscape?: boolean; // close dialog on escape button pressed
  onClose?: () => void;
  onClickOverlay?: () => void; // when background overlay is clicked, what to do
  // inline custom styling, override default style
  overlayStyle?: { [key: string]: unknown };
  contentClassName?: string;
  containerClassName?: string;
};

const DO_NOTHING = () => {};
const EMPTY_OBJECT = {};
const TRANSITION_DURATION = 200; //ms

function GenericPopup({
  children,
  shouldShow: shouldShowPopup,
  closeOnEscape = true,
  showOverlay = true,
  onClose = DO_NOTHING,
  onClickOverlay = onClose,
  overlayStyle = EMPTY_OBJECT, // feel free to add more if needed
  contentClassName,
  containerClassName,
}: Props) {
  // Hide popup on esc button pressed
  const onEscapePress = (event: React.KeyboardEvent<HTMLElement>): void => {
    // https://developer.mozilla.org/en-US/docs/Web/API/Document/keydown_event
    if (!event || event.nativeEvent.isComposing || event.keyCode === 229) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <Portal>
      <CSSTransition
        in={shouldShowPopup}
        timeout={TRANSITION_DURATION}
        mountOnEnter
        unmountOnExit
        classNames={{
          enter: styles.popupEnter,
          enterActive: styles.popupEnterActive,
          exit: styles.popupExit,
          exitActive: styles.popupExitActive,
        }}
      >
        <div
          className={cx(styles.container, containerClassName)}
          onKeyDown={closeOnEscape ? onEscapePress : undefined}
        >
          <div className={cx(styles.content, contentClassName)}>{children}</div>
        </div>
      </CSSTransition>
      <CSSTransition
        in={shouldShowPopup && showOverlay}
        timeout={TRANSITION_DURATION}
        mountOnEnter
        unmountOnExit
        classNames={{
          enter: styles.overlayEnter,
          enterActive: styles.overlayEnterActive,
          exit: styles.overlayExit,
          exitActive: styles.overlayExitActive,
        }}
      >
        <div
          className={styles.backgroundOverlay}
          onClick={onClickOverlay}
          style={{ ...overlayStyle }}
        />
      </CSSTransition>
    </Portal>
  );
}

export default GenericPopup;
